import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatINR, blurOnWheel } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { AlertTriangle, X } from "lucide-react";

/**
 * Phase F4 — Admin-only edit dialog for a pre-payment request.
 * Sends ONLY the fields that changed. Server enforces the material-vs-trivial
 * rule (material edit on an approved request bounces it back to pending).
 */
export default function EditRequestDialog({ req, onClose, onSaved }) {
  const [meta, setMeta] = useState({ sites: [], categories: [] });
  const [vendors, setVendors] = useState([]);
  const [form, setForm] = useState(() => ({
    amount: req.amount ?? "",
    vendor_id: req.vendor_id ?? "",
    category: req.category ?? "",
    site: req.site ?? "",
    notes: req.notes ?? "",
    urgency: req.urgency ?? "normal",
    scheduled_date: req.scheduled_date ?? "",
    requested_payment_mode: req.requested_payment_mode ?? "",
  }));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [m, v] = await Promise.all([
          api.get("/meta"),
          api.get("/vendors"),
        ]);
        setMeta(m.data);
        setVendors(v.data);
      } catch { /* meta is optional — the form still works with the original values */ }
    })();
  }, []);

  // Only send fields that actually changed vs the original.
  const delta = useMemo(() => {
    const out = {};
    const orig = {
      amount: req.amount ?? null,
      vendor_id: req.vendor_id ?? null,
      category: req.category ?? null,
      site: req.site ?? null,
      notes: req.notes ?? null,
      urgency: req.urgency ?? "normal",
      scheduled_date: req.scheduled_date ?? null,
      requested_payment_mode: req.requested_payment_mode ?? null,
    };
    for (const [k, v] of Object.entries(form)) {
      const norm = v === "" ? null : (k === "amount" ? Number(v) : v);
      if (norm !== orig[k]) out[k] = norm === null ? null : norm;
    }
    // Drop null-to-null (both empty) — those aren't real changes.
    for (const k of Object.keys(out)) {
      if (out[k] === null && (orig[k] === null || orig[k] === undefined)) delete out[k];
    }
    return out;
  }, [form, req]);

  const hasMaterialChange = useMemo(() => {
    return ["amount", "vendor_id", "category", "site"].some((k) => k in delta);
  }, [delta]);

  const willBounce = hasMaterialChange && req.status === "approved" && !req.confidential;
  const willCascade = hasMaterialChange && req.status === "approved" && req.confidential && "amount" in delta;

  const dirty = Object.keys(delta).length > 0;

  const save = async () => {
    if (!dirty) { toast.info("Nothing to save."); return; }
    setSaving(true);
    try {
      const payload = {};
      for (const [k, v] of Object.entries(delta)) {
        payload[k] = v;
      }
      const r = await api.patch(`/requests/${req.request_id}/edit`, payload);
      toast.success(willBounce
        ? "Request updated — moved back to pending for re-approval."
        : "Request updated. Requester notified.");
      onSaved(r.data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.message || "Save failed"));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-lg border border-slate-200 rounded-2xl p-0 overflow-hidden"
        data-testid="edit-request-dialog"
      >
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-slate-200">
          <DialogTitle className="font-display font-semibold text-lg tracking-tight text-slate-900">
            Edit request · {req.request_id}
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500 mt-1">
            Admin-only. Every change is audit-logged and the requester is notified.
          </DialogDescription>
        </DialogHeader>

        <div className="px-5 py-4 space-y-3 max-h-[65vh] overflow-y-auto">
          {(willBounce || willCascade) && (
            <div
              data-testid="edit-material-warning"
              className="flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900"
            >
              <AlertTriangle size={14} className="shrink-0 mt-0.5" />
              <div>
                {willBounce && (
                  <>
                    Material change on an <span className="font-semibold">approved</span> request.
                    Saving will <span className="font-semibold">move it back to pending</span> and
                    clear the current approval — an approver will need to re-approve.
                  </>
                )}
                {willCascade && (
                  <>
                    Amount change on a confidential (auto-approved) request. The approved amount
                    will be <span className="font-semibold">cascaded to match the new amount</span>.
                  </>
                )}
              </div>
            </div>
          )}

          <Row label="Amount">
            <input
              type="number"
              data-testid="edit-amount"
              onWheel={blurOnWheel}
              onKeyDown={(e) => { if (e.key === "ArrowUp" || e.key === "ArrowDown") e.preventDefault(); }}
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-olive/30"
            />
          </Row>

          <Row label="Vendor">
            <select
              data-testid="edit-vendor"
              value={form.vendor_id}
              onChange={(e) => setForm({ ...form, vendor_id: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm bg-white"
            >
              {vendors.map((v) => (
                <option key={v.vendor_id} value={v.vendor_id}>{v.name}</option>
              ))}
              {/* If the current vendor isn't in the (active) list, include it defensively */}
              {vendors.length > 0 && !vendors.find((v) => v.vendor_id === form.vendor_id) && form.vendor_id && (
                <option value={form.vendor_id}>{req.vendor_name} (current)</option>
              )}
            </select>
          </Row>

          <Row label="Category">
            <select
              data-testid="edit-category"
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm bg-white"
            >
              {(meta.categories || [form.category]).map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </Row>

          <Row label="Site">
            <select
              data-testid="edit-site"
              value={form.site}
              onChange={(e) => setForm({ ...form, site: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm bg-white"
            >
              {(meta.sites || [form.site]).map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </Row>

          <Row label="Payment mode (requested)">
            <div className="flex gap-2 flex-wrap">
              {["Cash", "UPI", "Bank Transfer"].map((m) => (
                <button
                  key={m}
                  type="button"
                  data-testid={`edit-mode-${m.replace(" ", "-")}`}
                  onClick={() => setForm({ ...form, requested_payment_mode: form.requested_payment_mode === m ? "" : m })}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition ${
                    form.requested_payment_mode === m
                      ? "bg-slate-900 text-white border-slate-900"
                      : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                  }`}
                >
                  {m}
                </button>
              ))}
            </div>
          </Row>

          <Row label="Urgency">
            <div className="flex gap-2">
              {["normal", "emergency"].map((u) => (
                <button
                  key={u}
                  type="button"
                  data-testid={`edit-urgency-${u}`}
                  onClick={() => setForm({ ...form, urgency: u })}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition ${
                    form.urgency === u
                      ? (u === "emergency" ? "bg-red-600 text-white border-red-600" : "bg-slate-900 text-white border-slate-900")
                      : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                  }`}
                >
                  {u}
                </button>
              ))}
            </div>
          </Row>

          <Row label="Scheduled date">
            <input
              type="date"
              data-testid="edit-scheduled"
              value={(form.scheduled_date || "").slice(0, 10)}
              onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm"
            />
          </Row>

          <Row label="Notes">
            <textarea
              data-testid="edit-notes"
              rows={2}
              value={form.notes || ""}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm resize-none"
            />
          </Row>
        </div>

        <div className="border-t border-slate-200 px-5 py-3 flex items-center justify-between">
          <div className="text-xs text-slate-500">
            {dirty ? `${Object.keys(delta).length} field${Object.keys(delta).length === 1 ? "" : "s"} changed` : "No changes"}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-xs font-medium text-slate-700 rounded-lg border border-slate-300 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="edit-save"
              onClick={save}
              disabled={!dirty || saving}
              className="px-4 py-2 text-xs font-semibold text-white rounded-lg bg-slate-900 hover:bg-black disabled:opacity-40 transition"
            >
              {saving ? "Saving…" : (willBounce ? "Save & re-queue" : "Save")}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const Row = ({ label, children }) => (
  <div>
    <div className="text-[10px] font-medium uppercase tracking-wider text-slate-500 mb-1">{label}</div>
    {children}
  </div>
);
